import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Gem, CheckCircle2, Hammer, Lock, Loader2, ShieldCheck, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import TagPhotos from '../components/TagPhotos';

// Phase 4 — per-pill classify UI for a single CSV record.
// Stock Supervisor picks Family → Origin → Form → Stone Name (cascading)
// driven by Gemstone Master + Family Rules.
// Variant required when the picked stone has variants on the master.
// Premium price OVERRIDES the regular price entirely (Q2=c).
//
// P3 — Premium-admin-only pricing (Feb 2026):
//   Pills auto-diverted from DV with `pricing_mode = "premium_admin_only"`
//   (solitaires & GIA-style codes) bypass the Gemstone Master cascade entirely
//   and are priced via a "Market rate" field that ONLY the admin role can edit.
//   Other roles see the pill but can't price it.

export default function ColourStoneVerification() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const isAdmin = (currentUser?.role || '').toLowerCase() === 'admin' ||
                  (currentUser?.role || '').toLowerCase() === 'owner';

  const [csv, setCsv]                 = useState(null);
  const [combos, setCombos]           = useState([]);
  const [stones, setStones]           = useState([]);
  const [rules, setRules]             = useState([]);
  const [makingStyles, setMakingStyles] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [savingIdx, setSavingIdx]     = useState(-1);
  const [savingMs, setSavingMs]       = useState(false);
  const [verifying, setVerifying]     = useState(false);
  const [photos, setPhotos]           = useState([]);
  // Local per-entry draft (controlled inputs)
  const [drafts, setDrafts]           = useState({});

  // ── Initial load ─────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [csvR, comboR, stoneR, ruleR, msR] = await Promise.all([
        api.get(`/colour-stone-verification/${id}`),
        api.get('/gemstone-master/combinations', { params: { include_inactive: false } }),
        api.get('/gemstone-master/stones'),
        api.get('/gemstone-master/family-rules'),
        api.get('/making-style-master', { params: { active_only: true } }),
      ]);
      setCsv(csvR.data);
      setCombos(comboR.data.items || []);
      setStones(stoneR.data.items || []);
      setRules(ruleR.data.items || []);
      setMakingStyles(msR.data.items || []);
      setPhotos(csvR.data.photos || []);
      // Hydrate drafts from existing classifications
      const next = {};
      (csvR.data.entries || []).forEach((e, i) => {
        const combo = (comboR.data.items || []).find(c => c.id === e.combination_id);
        next[i] = {
          family:        combo?.family     || '',
          origin:        combo?.origin     || '',
          form:          combo?.form       || '',
          stone_name:    combo?.stone_name || '',
          variant:       e.variant         || '',
          premium_price: e.premium_price ?? '',
        };
      });
      setDrafts(next);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load record');
    } finally {
      setLoading(false);
    }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  // ── Derived ──────────────────────────────────────────────────────────────
  const families = useMemo(() => rules.map(r => r.family).sort(), [rules]);
  const rulesByFamily = useMemo(() => {
    const m = {};
    rules.forEach(r => { m[r.family] = r; });
    return m;
  }, [rules]);

  const allowedOrigins = (fam) => {
    const r = rulesByFamily[fam];
    if (!r) return [];
    // Intersect rulebook with origins actually present in combinations for this family
    const present = new Set(combos.filter(c => c.family === fam).map(c => c.origin));
    return (r.allowed_origins || []).filter(o => present.has(o)).sort();
  };
  const allowedForms = (fam, org) => {
    const r = rulesByFamily[fam];
    if (!r) return [];
    const present = new Set(
      combos.filter(c => c.family === fam && (!org || c.origin === org)).map(c => c.form)
    );
    return (r.allowed_forms || []).filter(f => present.has(f)).sort();
  };
  const stoneNamesFor = (fam, org, frm) => {
    if (!fam || !org || !frm) return [];
    return [...new Set(
      combos.filter(c => c.family === fam && c.origin === org && c.form === frm)
            .map(c => c.stone_name)
    )].sort();
  };
  const findCombo = (fam, org, frm, sn) =>
    combos.find(c => c.family === fam && c.origin === org && c.form === frm && c.stone_name === sn);
  const variantsForStone = (sn) => {
    if (!sn) return [];
    const st = stones.find(s => s.stone_name === sn);
    if (!st || !(st.variant || '').trim()) return [];
    return st.variant.split(',').map(v => v.trim()).filter(Boolean);
  };

  // ── Patch helpers ────────────────────────────────────────────────────────
  const patchDraft = (i, partial) => setDrafts(d => ({
    ...d,
    [i]: { ...d[i], ...partial },
  }));

  // ── One-click verify (Phase: removes per-pill Save) ──────────────────────
  // Collects every draft, validates locally for fast feedback, then POSTs a
  // single bulk payload — the backend re-validates atomically.
  const buildBulkPayload = () => {
    const out = [];
    const errors = [];
    (csv?.entries || []).forEach((entry, i) => {
      const d = drafts[i] || {};
      const isPremiumAdmin = (entry.pricing_mode === 'premium_admin_only');

      if (isPremiumAdmin) {
        // P3 — solitaire / GIA-style: skip cascade, require admin + market rate
        if (!isAdmin) {
          errors.push(`Pill ${i + 1}: requires admin pricing (market rate). Only the admin role can verify this tag.`);
          return;
        }
        const premiumRaw = d.premium_price;
        if (premiumRaw === '' || premiumRaw == null) {
          errors.push(`Pill ${i + 1}: market rate is required for admin-priced pills`);
          return;
        }
        const premium = Number(premiumRaw);
        if (isNaN(premium) || premium <= 0) {
          errors.push(`Pill ${i + 1}: market rate must be a positive number`);
          return;
        }
        out.push({
          idx:            i,
          combination_id: null,
          variant:        '',
          premium_price:  premium,
        });
        return;
      }

      const combo = findCombo(d.family, d.origin, d.form, d.stone_name);
      if (!combo) {
        errors.push(`Pill ${i + 1}: pick Family → Origin → Form → Stone Name`);
        return;
      }
      const variantOptions = variantsForStone(d.stone_name);
      if (variantOptions.length && !d.variant) {
        errors.push(`Pill ${i + 1}: variant required for ${d.stone_name}`);
        return;
      }
      const premiumRaw = d.premium_price;
      const premium = (premiumRaw === '' || premiumRaw == null) ? null : Number(premiumRaw);
      if (premium !== null && (isNaN(premium) || premium < 0)) {
        errors.push(`Pill ${i + 1}: premium price must be ≥ 0`);
        return;
      }
      out.push({
        idx:            i,
        combination_id: combo.id,
        variant:        d.variant || '',
        premium_price:  premium,
      });
    });
    return { entries: out, errors };
  };

  const verifyAll = async () => {
    if (!csv?.making_style_id) {
      toast.error('Please pick a Making Style before verifying this tag.');
      return;
    }
    const { entries, errors } = buildBulkPayload();
    if (errors.length) {
      toast.error(errors[0]);  // first error wins — user fixes top-down
      return;
    }
    setVerifying(true);
    try {
      await api.post(`/colour-stone-verification/${id}/verify-all`, { entries });
      toast.success('Tag verified — salesman notified');
      navigate('/colour-stone-verification');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Verify failed');
    } finally {
      setVerifying(false);
    }
  };

  // Save the Making Style picker selection (immediate PATCH so it survives refresh).
  const saveMakingStyle = async (msId) => {
    setSavingMs(true);
    try {
      await api.patch(`/colour-stone-verification/${id}`, { making_style_id: msId || '' });
      // Reflect locally so verify button gate updates without a full reload.
      const picked = makingStyles.find(m => m.id === msId);
      setCsv(prev => ({
        ...prev,
        making_style_id:   msId || null,
        making_style_name: picked ? picked.name : null,
      }));
      toast.success(msId ? `Making Style: ${picked?.name || ''}` : 'Making Style cleared');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save Making Style');
    } finally {
      setSavingMs(false);
    }
  };

  const restoreEntry = async (i) => {
    if (!window.confirm(
      `Bring this pill back to Diamond Verification? ` +
      `(Only works while the pill is unclassified and the CSV is still draft.)`
    )) return;
    setSavingIdx(i);
    try {
      const r = await api.delete(`/colour-stone-verification/${id}/entries/${i}/restore-to-dv`);
      toast.success(`Pill restored — back in DV.`);
      if ((r.data?.csv_remaining_entries ?? 0) === 0) {
        navigate('/colour-stone-verification');
      } else {
        await load();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Restore failed');
    } finally {
      setSavingIdx(-1);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-400 text-sm">
        <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading record…
      </div>
    );
  }
  if (!csv) {
    return <div className="min-h-screen flex items-center justify-center text-gray-400 text-sm">Record not found.</div>;
  }

  const isVerified = csv.status === 'verified';

  // Live "ready" check — reflects local drafts so the gate updates as the user types.
  const isEntryReady = (i) => {
    const entry = (csv?.entries || [])[i] || {};
    const d = drafts[i] || {};
    // P3 — premium-admin-only pills: ready iff admin has entered a positive market rate
    if (entry.pricing_mode === 'premium_admin_only') {
      if (!isAdmin) return false;
      const p = d.premium_price;
      if (p === '' || p == null) return false;
      const num = Number(p);
      return !isNaN(num) && num > 0;
    }
    const combo = findCombo(d.family, d.origin, d.form, d.stone_name);
    if (!combo) return false;
    const variantOptions = variantsForStone(d.stone_name);
    if (variantOptions.length && !d.variant) return false;
    const p = d.premium_price;
    if (p !== '' && p != null && (isNaN(Number(p)) || Number(p) < 0)) return false;
    return true;
  };
  const readyCount = (csv.entries || []).reduce((n, _e, i) => n + (isEntryReady(i) ? 1 : 0), 0);
  const totalCount = (csv.entries || []).length;
  const unclassified = totalCount - readyCount;
  const allEntriesReady = totalCount === 0 || readyCount === totalCount;
  const msPicked = !!csv.making_style_id;
  const allReady = allEntriesReady && msPicked;

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50/40 via-rose-50/30 to-white pb-28">
      {/* Sticky header */}
      <div className="sticky top-0 z-10 bg-white/95 backdrop-blur border-b border-gray-100">
        <div className="px-3 py-2.5 flex items-center justify-between">
          <button
            onClick={() => navigate('/colour-stone-verification')}
            className="p-2 -ml-2 rounded-lg hover:bg-gray-50"
            data-testid="csv-detail-back"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <Gem className="w-4 h-4 text-amber-600" />
            <div className="text-center">
              <h1 className="text-sm font-bold text-gray-800">Tag {csv.tag_number}</h1>
              <p className="text-[10px] text-gray-500">{csv.entries.length} pill(s) · {csv.branch || 'no branch'}</p>
            </div>
          </div>
          <div className="w-9" />
        </div>
        {isVerified && (
          <div className="bg-emerald-50 border-t border-emerald-100 px-3 py-1.5 flex items-center justify-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-700" />
            <span className="text-[11px] font-bold text-emerald-700">Verified · read-only</span>
          </div>
        )}
      </div>

      {/* Pill list */}
      <div className="px-3 py-4 max-w-3xl mx-auto space-y-3">

        {/* Reference photos (Phase 6) — Stock Supervisor can attach up to 3 photos */}
        <TagPhotos
          recordId={csv.id}
          endpointBase="/api/colour-stone-verification"
          photos={photos}
          setPhotos={setPhotos}
          maxPhotos={3}
          disabled={isVerified}
          title="Reference Photos"
          emptyHint="Attach photos of the tag so others can confirm your colour-stone picks."
          testidPrefix="csv-photos"
        />

        {/* Making Style picker — mandatory for every tag (drives Estimate's
            wastage % + making/gm + minimum making). NOTE: this is NOT the
            ERP "Design Style". Two separate concepts — keep them clear. */}
        <div
          className={`bg-white rounded-xl border ${csv.making_style_id ? 'border-indigo-100' : 'border-amber-300 ring-1 ring-amber-200'} p-3 shadow-sm`}
          data-testid="csv-making-style-block"
        >
          <div className="flex items-center gap-2 mb-2">
            <Hammer className="w-4 h-4 text-indigo-600" />
            <div className="flex-1 min-w-0">
              <h3 className="text-xs font-bold text-gray-800 uppercase tracking-wide">Making Style</h3>
              <p className="text-[10px] text-gray-500 leading-tight">
                Drives wastage %, making/gm & min making on the Estimate.
                Mandatory to verify this tag.
              </p>
            </div>
            {csv.making_style_id && (
              <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-100 px-1.5 py-0.5 rounded">
                ✓ Picked
              </span>
            )}
          </div>
          {isVerified ? (
            <div
              className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm font-bold text-slate-800"
              data-testid="csv-making-style-frozen"
            >
              {csv.making_style_name || '—'}
            </div>
          ) : (
            <select
              value={csv.making_style_id || ''}
              onChange={e => saveMakingStyle(e.target.value)}
              disabled={savingMs}
              className={`w-full px-3 py-2 border rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500 ${csv.making_style_id ? 'border-indigo-200 bg-indigo-50/40' : 'border-amber-300 bg-amber-50/40'}`}
              data-testid="csv-making-style-select"
            >
              <option value="">— Pick a Making Style —</option>
              {makingStyles.map(m => (
                <option key={m.id} value={m.id} data-testid={`csv-ms-opt-${m.id}`}>
                  {m.name} · {m.wastage_percentage}% · ₹{m.making_charges_per_gram}/gm · min ₹{m.minimum_making_charges?.toLocaleString('en-IN') || 0}
                </option>
              ))}
            </select>
          )}
          {!isVerified && makingStyles.length === 0 && (
            <p className="text-[11px] text-amber-700 mt-2 font-medium">
              No Making Styles configured yet. Ask an admin to add some in
              Masters → Making Style Master.
            </p>
          )}
        </div>

        {csv.entries.map((entry, i) => {
          const d = drafts[i] || {};
          const combo = findCombo(d.family, d.origin, d.form, d.stone_name);
          const regular = combo?.regular_price;
          const premiumNum = (d.premium_price === '' || d.premium_price === null || d.premium_price === undefined)
            ? null : Number(d.premium_price);
          const finalPrice = (premiumNum !== null && !isNaN(premiumNum)) ? premiumNum : regular;
          const isPolki = (entry.erp_stone_name || '').toUpperCase().includes('POLKI');
          const variantOptions = variantsForStone(d.stone_name);
          const requiresVariant = variantOptions.length > 0;
          const isPremiumAdmin = (entry.pricing_mode === 'premium_admin_only');

          const entryReady = isVerified
            ? (isPremiumAdmin ? entry.premium_price != null : !!entry.combination_id)
            : isEntryReady(i);
          return (
            <div
              key={i}
              className={`bg-white rounded-xl border shadow-sm overflow-hidden ${
                entryReady ? 'border-emerald-100' : 'border-gray-100'
              }`}
              data-testid={`csv-entry-${i}`}
            >
              {/* Pill header */}
              <div className={`px-3 py-2 border-b border-gray-100 flex items-center justify-between ${
                entryReady ? 'bg-emerald-50/40' : 'bg-gray-50/40'
              }`}>
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-[11px] font-bold text-gray-500">PILL {i + 1}</span>
                  {entry.from_dv && <span className="text-[10px] font-bold text-purple-700 bg-purple-100 px-1.5 py-0.5 rounded-full">FROM DV</span>}
                  {isPremiumAdmin && (
                    <span className="text-[10px] font-bold text-amber-900 bg-amber-200 px-1.5 py-0.5 rounded-full" data-testid={`csv-premium-badge-${i}`}>
                      PREMIUM · ADMIN
                    </span>
                  )}
                  {isPolki && !entry.from_dv && !isPremiumAdmin && <span className="text-[10px] font-bold text-purple-700 bg-purple-100 px-1.5 py-0.5 rounded-full">POLKI</span>}
                  {!entry.erp_is_diamond && !isPolki && !entry.from_dv && !isPremiumAdmin && (
                    <span className="text-[10px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full">SILENT</span>
                  )}
                  <span className="text-[11px] text-gray-700 truncate" title={entry.erp_stone_name}>
                    {entry.erp_stone_name || '—'}
                  </span>
                </div>
                {entryReady ? (
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" data-testid={`csv-ready-${i}`} />
                ) : (
                  <span className="text-[10px] font-bold text-gray-400 shrink-0">pending</span>
                )}
              </div>

              {/* Pill body */}
              <div className="p-3 space-y-2.5">
                <div className="grid grid-cols-3 gap-2 text-[11px]">
                  <div><span className="text-gray-400">Pieces</span><div className="font-bold text-gray-800">{entry.pieces}</div></div>
                  <div><span className="text-gray-400">Weight</span><div className="font-bold text-gray-800">{entry.weight_ct} ct</div></div>
                  <div><span className="text-gray-400">ERP Combo</span><div className="font-bold text-gray-800">{entry.erp_combo_id || '—'}</div></div>
                </div>

                {/* Cascading dropdowns — skipped entirely for premium-admin-only pills */}
                {!isPremiumAdmin && (
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Family">
                    <select
                      disabled={isVerified}
                      value={d.family || ''}
                      onChange={(e) => patchDraft(i, { family: e.target.value, origin: '', form: '', stone_name: '', variant: '' })}
                      className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg bg-white disabled:bg-gray-50 disabled:text-gray-400"
                      data-testid={`csv-family-${i}`}
                    >
                      <option value="">— pick —</option>
                      {families.map(f => <option key={f} value={f}>{f}</option>)}
                    </select>
                  </Field>

                  <Field label="Origin">
                    <select
                      disabled={isVerified || !d.family}
                      value={d.origin || ''}
                      onChange={(e) => patchDraft(i, { origin: e.target.value, form: '', stone_name: '', variant: '' })}
                      className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg bg-white disabled:bg-gray-50 disabled:text-gray-400"
                      data-testid={`csv-origin-${i}`}
                    >
                      <option value="">— pick —</option>
                      {allowedOrigins(d.family).map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </Field>

                  <Field label="Form">
                    <select
                      disabled={isVerified || !d.origin}
                      value={d.form || ''}
                      onChange={(e) => patchDraft(i, { form: e.target.value, stone_name: '', variant: '' })}
                      className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg bg-white disabled:bg-gray-50 disabled:text-gray-400"
                      data-testid={`csv-form-${i}`}
                    >
                      <option value="">— pick —</option>
                      {allowedForms(d.family, d.origin).map(f => <option key={f} value={f}>{f}</option>)}
                    </select>
                  </Field>

                  <Field label="Stone Name">
                    <select
                      disabled={isVerified || !d.form}
                      value={d.stone_name || ''}
                      onChange={(e) => patchDraft(i, { stone_name: e.target.value, variant: '' })}
                      className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg bg-white disabled:bg-gray-50 disabled:text-gray-400"
                      data-testid={`csv-stone-${i}`}
                    >
                      <option value="">— pick —</option>
                      {stoneNamesFor(d.family, d.origin, d.form).map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </Field>

                  {requiresVariant && (
                    <Field label={`Variant *`} className="col-span-2">
                      <select
                        disabled={isVerified}
                        value={d.variant || ''}
                        onChange={(e) => patchDraft(i, { variant: e.target.value })}
                        className="w-full px-2 py-1.5 text-sm border border-amber-300 rounded-lg bg-amber-50/40 disabled:bg-gray-50 disabled:text-gray-400"
                        data-testid={`csv-variant-${i}`}
                      >
                        <option value="">— pick —</option>
                        {variantOptions.map(v => <option key={v} value={v}>{v}</option>)}
                      </select>
                    </Field>
                  )}
                </div>
                )}

                {/* Price row — premium-admin-only uses a focused "Market rate" block */}
                {isPremiumAdmin ? (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 space-y-1.5" data-testid={`csv-premium-admin-block-${i}`}>
                    <p className="text-[11px] font-bold text-amber-900 flex items-center gap-1">
                      <Lock className="w-3 h-3" />
                      Premium pricing — admin-only
                    </p>
                    <p className="text-[10px] text-amber-800/80">
                      This stone (auto-routed from DV) needs a market-rate price set by the admin.
                      The Gemstone Master does not apply here.
                    </p>
                    <Field label="Market rate (₹)">
                      <input
                        type="number"
                        step="0.01"
                        min="0.01"
                        disabled={isVerified || !isAdmin}
                        placeholder={isAdmin ? 'Enter current market price' : 'Awaiting admin pricing'}
                        value={d.premium_price ?? ''}
                        onChange={(e) => patchDraft(i, { premium_price: e.target.value })}
                        className="w-full px-2 py-1.5 text-sm border border-amber-300 rounded-lg bg-white disabled:bg-amber-100/50 disabled:text-gray-500"
                        data-testid={`csv-market-rate-${i}`}
                      />
                    </Field>
                    {!isAdmin && !isVerified && (
                      <p className="text-[10px] text-amber-900 font-semibold">
                        Only the admin role can price this pill.
                      </p>
                    )}
                    <div className="flex items-center justify-between text-[12px] pt-1 border-t border-amber-200">
                      <span className="font-bold text-gray-700">Final price</span>
                      <span className="font-bold text-emerald-700" data-testid={`csv-final-${i}`}>
                        {premiumNum != null && !isNaN(premiumNum) ? `₹ ${Number(premiumNum).toLocaleString('en-IN')}` : '—'}
                      </span>
                    </div>
                  </div>
                ) : (
                <div className="bg-gray-50 rounded-lg p-2.5 space-y-1.5">
                  <div className="flex items-center justify-between text-[11px]">
                    <span className="text-gray-500">Regular (master)</span>
                    <span className="font-bold text-gray-700">
                      {regular != null ? `₹ ${Number(regular).toLocaleString('en-IN')}` : '—'}
                    </span>
                  </div>
                  <Field label="Premium override (replaces regular)">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      disabled={isVerified}
                      placeholder="leave blank to use regular"
                      value={d.premium_price ?? ''}
                      onChange={(e) => patchDraft(i, { premium_price: e.target.value })}
                      className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg bg-white disabled:bg-gray-50 disabled:text-gray-400"
                      data-testid={`csv-premium-${i}`}
                    />
                  </Field>
                  <div className="flex items-center justify-between text-[12px] pt-1 border-t border-gray-200">
                    <span className="font-bold text-gray-700">Final price</span>
                    <span className="font-bold text-emerald-700" data-testid={`csv-final-${i}`}>
                      {finalPrice != null && !isNaN(finalPrice) ? `₹ ${Number(finalPrice).toLocaleString('en-IN')}` : '—'}
                    </span>
                  </div>
                  {isVerified && entry.regular_price_frozen != null && (
                    <div className="flex items-center justify-between text-[10px] text-gray-400 pt-1 border-t border-gray-100">
                      <span>Frozen at verify</span>
                      <span>₹ {Number(entry.regular_price_frozen).toLocaleString('en-IN')}</span>
                    </div>
                  )}
                </div>
                )}

                {/* Bring back to DV — only for diverted, unclassified pills, while CSV is draft */}
                {!isVerified && entry.from_dv && !entry.combination_id && (
                  <button
                    onClick={() => restoreEntry(i)}
                    disabled={savingIdx === i}
                    className="w-full py-2 rounded-lg border-2 border-purple-200 text-purple-700 text-xs font-bold flex items-center justify-center gap-1.5 hover:bg-purple-50 disabled:opacity-50"
                    data-testid={`csv-restore-${i}`}
                  >
                    <Undo2 className="w-3.5 h-3.5" /> Bring back to Diamond Verification
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Sticky verify bar */}
      {!isVerified && (
        <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur border-t border-gray-100 p-3 z-20">
          <div className="max-w-3xl mx-auto flex items-center justify-between gap-3">
            <div className="text-xs">
              {totalCount === 0 ? (
                <p className="font-bold text-gray-800">No colour-stone pills</p>
              ) : (
                <p className="font-bold text-gray-800">{unclassified} of {totalCount} pending</p>
              )}
              <p className="text-[10px] text-gray-400">
                {!msPicked ? 'Pick a Making Style to enable verify.' : 'Verify locks the tag for this verification.'}
              </p>
            </div>
            <button
              onClick={verifyAll}
              disabled={!allReady || verifying}
              className="px-5 py-2.5 rounded-lg bg-emerald-600 text-white text-sm font-bold flex items-center gap-1.5 disabled:opacity-50 disabled:bg-gray-300"
              data-testid="csv-verify-btn"
            >
              {verifying
                ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Verifying…</>
                : <><Lock className="w-3.5 h-3.5" /> Verify Tag</>}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children, className = '' }) {
  return (
    <div className={className}>
      <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wide mb-0.5">{label}</label>
      {children}
    </div>
  );
}
